import { DateTime } from 'luxon';
import { initializeSelect } from './manuals/form';

export function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) === name + '=') {
                cookieValue = decodeURIComponent(
                    cookie.substring(name.length + 1),
                );
                break;
            }
        }
    }
    return cookieValue;
}

export function debounce(func, timeout = 300) {
    /*
      // Example usage:
      const setFilters = debounce((e) => {
        table.setFilter(e.target.name, "=", e.target.value);
      }, 300);
      input.addEventListener("change", function (e) {
        setFilters(e);
      });
  */
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, timeout);
    };
}

export function toggleAreaExpanded(selector) {
    let element;
    if (typeof selector === 'string') {
        element = document.querySelector(selector);
    } else if (typeof selector === 'object') {
        element = selector;
    }
    !(element.getAttribute('aria-expanded') == 'true')
        ? element.setAttribute('aria-expanded', 'true')
        : element.setAttribute('aria-expanded', 'false');
}

export function handleMobileMenu() {
    const menu_btn = document.getElementById('mobile-menu-btn');
    const menu = document.getElementById('main-nav');

    menu_btn.addEventListener('click', (e) => {
        e.preventDefault();
        toggleAreaExpanded(menu_btn);
        menu.classList.toggle('opened');
    });

    hideOnClickOutside(menu, menu_btn);
}

const isVisible = (elem) =>
    !!elem &&
    !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
const isActive = (element, parent) => {
    return (
        element.classList.contains('opened') ||
        element.classList.contains('active') ||
        parent.classList.contains('opened') ||
        parent.classList.contains('active')
    );
};

export function hideOnClickOutside(element, parent) {
    const outsideClickListener = (event) => {
        if (
            !parent.contains(event.target) &&
            !element.contains(event.target) &&
            isVisible(element)
        ) {
            if (
                isActive(element, parent) &&
                parent.querySelector('[aria-expanded]')
            ) {
                toggleAreaExpanded(parent.querySelector('[aria-expanded]'));
            }
            if (
                isActive(element, parent) &&
                parent.hasAttribute('aria-expanded')
            ) {
                toggleAreaExpanded(parent);
            }
            element.classList.remove('opened');
            element.classList.remove('active');
            parent.classList.remove('opened');
            parent.classList.remove('active');
        }
    };
    document.addEventListener('click', outsideClickListener);
}

export function initializeClock() {
    const clockElement = document.querySelector('.clock');
    function updateClock() {
        const currentTime = DateTime.now();
        clockElement.textContent = currentTime.toFormat('HH:mm');
    }

    updateClock();
    setInterval(updateClock, 1000);
}

export async function sendDeleteRequest(url) {
    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFTOKEN': getCookie('csrftoken'),
        },
    });
    if (!response.ok) {
        const errorData = await response
            .json()
            .catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || 'Delete request failed');
    }
    return response;
}

export async function sendPostRequest(url, data) {
    const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFTOKEN': getCookie('csrftoken'),
        },
    });

    if (!response.ok) {
        const errorData = await response
            .json()
            .catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || 'POST request failed');
    }
    return response;
}

export async function sendFormPostRequest(url, formData) {
    const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
            'X-CSRFTOKEN': getCookie('csrftoken'),
        },
    });
    if (!response.ok) {
        const errorData = await response
            .json()
            .catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || 'Form POST request failed');
    }
    return response;
}

export async function sendPatchRequest(url, data) {
    const response = await fetch(url, {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFTOKEN': getCookie('csrftoken'),
        },
    });

    if (!response.ok) {
        const errorData = await response
            .json()
            .catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || 'PATCH request failed');
    }
    return response;
}

export function truncateString(str, num) {
    if (!str || str.length <= num) {
        return str || '';
    }
    return str.slice(0, num) + '...';
}

export function initializeDependentGroupTypeSelects(groupSelect, typeSelect) {
    if (!groupSelect || !typeSelect) {
        console.warn(
            'initializeDependentGroupTypeSelects: Group or Type select element not provided.',
        );
        return;
    }

    // --- 1. Parse Type Select Options based on Optgroup Labels ---
    const typeOptgroups = typeSelect.querySelectorAll('optgroup');
    const groupTypeMap = new Map();
    typeOptgroups.forEach((optgroup) => {
        const groupLabel = optgroup.label;
        const options = [];
        optgroup.querySelectorAll('option').forEach((option) => {
            options.push({
                value: option.value,
                label: option.textContent || option.value,
            });
        });
        if (groupLabel) {
            groupTypeMap.set(groupLabel, options);
        }
    });

    // --- 2. Initialize the Select Elements ---
    initializeSelect(groupSelect, {}, true);
    initializeSelect(typeSelect, {}, true);

    // --- 3. Get Choices.js instances ---
    const groupChoices = groupSelect._choices;
    const typeChoices = typeSelect._choices;

    if (!groupChoices || !typeChoices) {
        console.error(
            'initializeDependentGroupTypeSelects: Choices.js instances not found on select elements.',
        );
        return;
    }

    // --- 4. Define the Change Handler ---
    const handleGroupSelectChange = () => {
        const selectedGroup = groupChoices.getValue();
        const groupValue = selectedGroup ? selectedGroup.value : null;
        const groupLabel = selectedGroup ? selectedGroup.label : null;

        const selectedTypeObject = typeChoices.getValue();
        const selectedTypeValue = selectedTypeObject
            ? selectedTypeObject.value
            : null;

        if (!groupValue) {
            typeChoices.disable();
            typeChoices.clearStore();
            typeChoices.clearInput();
            typeChoices.setChoices(
                [
                    {
                        value: '',
                        label: gettext('Najpierw należy wybrać grupę'),
                        selected: true,
                        disabled: true,
                        placeholder: true,
                    },
                ],
                'value',
                'label',
                true,
            );
        } else {
            const optionsForGroup =
                groupTypeMap.get(groupValue) ||
                groupTypeMap.get(groupLabel) ||
                [];

            const isPreviousTypeValid = optionsForGroup.some(
                (option) => option.value && option.value === selectedTypeValue,
            );

            const valueToSet = isPreviousTypeValid ? selectedTypeValue : '';

            typeSelect.innerHTML = '';

            const placeholderOption = new Option('-----', '');
            typeSelect.add(placeholderOption);

            optionsForGroup.forEach((opt) => {
                const optionElement = new Option(opt.label, opt.value);
                typeSelect.add(optionElement);
            });

            typeChoices.setChoices(
                Array.from(typeSelect.options).map((opt) => ({
                    value: opt.value,
                    label: opt.text,
                })),
                'value',
                'label',
                true,
            );

            typeChoices.setChoiceByValue(valueToSet);
            typeChoices.enable();
        }
    };

    // --- 5. Set Initial State and Attach Listener ---
    handleGroupSelectChange();
    groupSelect.addEventListener('change', handleGroupSelectChange);
}

export const hasPermission = (permission) => {
    const userPermissionsData = JSON.parse(
        document.querySelector('body').dataset.permissions,
    );
    return userPermissionsData.includes(permission);
};

export default {
    getCookie,
    debounce,
    toggleAreaExpanded,
    handleMobileMenu,
    hideOnClickOutside,
    initializeClock,
    sendDeleteRequest,
    sendPatchRequest,
    sendPostRequest,
    initializeDependentGroupTypeSelects,
    hasPermission,
};
